import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useSessionTracker } from "@/hooks/useSessionTracker";

export interface AuthWallet {
  id: string;
  wallet_address: string;
  wallet_name: string;
  status: string;
  backup_verified: boolean;
  is_active: boolean;
  created_at?: string;
}

interface AuthSecurity {
  totp_enabled: boolean;
  pin_enabled: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  walletsLoading: boolean;
  wallet: AuthWallet | null;
  wallets: AuthWallet[];
  security: AuthSecurity | null;
  securityVerified: boolean;
  setSecurityVerified: (verified: boolean) => void;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  generateWallet: (walletName?: string) => Promise<{ mnemonic: string; address: string } | null>;
  importWallet: (importType: "seed" | "private_key", importValue: string, walletName?: string) => Promise<{ address: string } | null>;
  switchWallet: (walletId: string) => Promise<boolean>;
  deleteWallet: (walletId: string) => Promise<{ success: boolean; error?: string }>;
  renameWallet: (walletId: string, newName: string) => Promise<boolean>;
  verifyBackup: (words: string, walletId?: string) => Promise<boolean>;
  setupPin: (pin: string) => Promise<boolean>;
  disablePin: (pin: string) => Promise<{ success: boolean; error?: string }>;
  verifyPin: (pin: string) => Promise<{ success: boolean; error?: string; locked_until?: string; attempts_left?: number }>;
  setupTotp: () => Promise<{ secret: string; qr_uri: string } | null>;
  verifyTotp: (code: string) => Promise<boolean>;
  verifyTotpCode: (code: string) => Promise<boolean>;
  disableTotp: (code: string) => Promise<{ success: boolean; error?: string }>;
  exportSeed: (pin?: string, walletId?: string) => Promise<string | null>;
  refreshWallet: () => Promise<void>;
  refreshWallets: () => Promise<void>;
  refreshSecurity: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const getEncryptionKey = (userId: string): string => {
  return `tronscope_${userId}_2024`;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [walletsLoading, setWalletsLoading] = useState(true);
  const [wallet, setWallet] = useState<AuthWallet | null>(null);
  const [wallets, setWallets] = useState<AuthWallet[]>([]);
  const [security, setSecurity] = useState<AuthSecurity | null>(null);
  const [securityVerified, setSecurityVerified] = useState(false);

  // Track user session for admin analytics
  useSessionTracker(user?.id);

  const refreshWallet = async (currentUser?: User | null) => {
    const targetUser = currentUser ?? user;
    if (!targetUser) return;
    
    try {
      const response = await supabase.functions.invoke("auth-wallet", {
        body: { action: "get_wallet", user_id: targetUser.id },
      });
      
      if (response.data?.success && response.data?.wallet) {
        setWallet(response.data.wallet);
      } else {
        setWallet(null);
      }
    } catch (error) {
      console.error("Failed to refresh wallet:", error);
    }
  };

  const refreshWallets = async (currentUser?: User | null) => {
    const targetUser = currentUser ?? user;
    if (!targetUser) {
      setWalletsLoading(false);
      return;
    }
    
    setWalletsLoading(true);
    try {
      console.log("Refreshing wallets for user:", targetUser.id);
      const response = await supabase.functions.invoke("auth-wallet", {
        body: { action: "get_wallets", user_id: targetUser.id },
      });
      
      console.log("Refresh wallets response:", response.data);
      
      if (response.data?.success && response.data?.wallets) {
        setWallets(response.data.wallets);
        // Also update active wallet
        const activeWallet = response.data.wallets.find((w: AuthWallet) => w.is_active);
        if (activeWallet) {
          setWallet(activeWallet);
        }
      }
    } catch (error) {
      console.error("Failed to refresh wallets:", error);
    } finally {
      setWalletsLoading(false);
    }
  };

  const refreshSecurity = async (currentUser?: User | null) => {
    const targetUser = currentUser ?? user;
    if (!targetUser) return;
    
    try {
      const response = await supabase.functions.invoke("auth-wallet", {
        body: { action: "get_security", user_id: targetUser.id },
      });
      
      if (response.data?.success && response.data?.security) {
        setSecurity(response.data.security);
      }
    } catch (error) {
      console.error("Failed to refresh security:", error);
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Auth state changed:", event, session?.user?.id);
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        if (session?.user) {
          // Pass the user directly to avoid stale closure
          setTimeout(() => {
            refreshWallets(session.user);
            refreshSecurity(session.user);
          }, 0);
        } else {
          setWallet(null);
          setWallets([]);
          setSecurity(null);
          setWalletsLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log("Initial session check:", session?.user?.id);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      if (session?.user) {
        // Pass the user directly to avoid stale closure
        setTimeout(() => {
          refreshWallets(session.user);
          refreshSecurity(session.user);
        }, 0);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setWallet(null);
    setWallets([]);
    setSecurity(null);
    setSecurityVerified(false);
  };

  const generateWallet = async (walletName?: string) => {
    if (!user) return null;
    
    try {
      const response = await supabase.functions.invoke("auth-wallet", {
        body: {
          action: "generate_wallet",
          user_id: user.id,
          encryption_key: getEncryptionKey(user.id),
          wallet_name: walletName,
        },
      });
      
      if (response.data?.success) {
        await refreshWallets();
        return {
          mnemonic: response.data.mnemonic,
          address: response.data.address,
        };
      }
      
      throw new Error(response.data?.error || "Failed to generate wallet");
    } catch (error) {
      console.error("Generate wallet error:", error);
      return null;
    }
  };

  const importWallet = async (importType: "seed" | "private_key", importValue: string, walletName?: string) => {
    if (!user) return null;
    
    try {
      const response = await supabase.functions.invoke("auth-wallet", {
        body: {
          action: "import_wallet",
          user_id: user.id,
          encryption_key: getEncryptionKey(user.id),
          import_type: importType,
          import_value: importValue,
          wallet_name: walletName,
        },
      });
      
      if (response.data?.success) {
        await refreshWallets();
        return { address: response.data.address };
      }
      
      throw new Error(response.data?.error || "Failed to import wallet");
    } catch (error) {
      console.error("Import wallet error:", error);
      throw error;
    }
  };

  const switchWallet = async (walletId: string) => {
    if (!user) return false;
    
    try {
      const response = await supabase.functions.invoke("auth-wallet", {
        body: {
          action: "switch_wallet",
          user_id: user.id,
          wallet_id: walletId,
        },
      });
      
      if (response.data?.success) {
        await refreshWallets();
        return true;
      }
      return false;
    } catch (error) {
      console.error("Switch wallet error:", error);
      return false;
    }
  };

  const deleteWallet = async (walletId: string) => {
    if (!user) return { success: false, error: "Not authenticated" };
    
    try {
      const response = await supabase.functions.invoke("auth-wallet", {
        body: {
          action: "delete_wallet",
          user_id: user.id,
          wallet_id: walletId,
        },
      });
      
      if (response.data?.success) {
        await refreshWallets();
        return { success: true };
      }
      return { success: false, error: response.data?.error || "Failed to delete wallet" };
    } catch (error) {
      console.error("Delete wallet error:", error);
      return { success: false, error: "Failed to delete wallet" };
    }
  };

  const renameWallet = async (walletId: string, newName: string) => {
    if (!user) return false;
    
    try {
      const response = await supabase.functions.invoke("auth-wallet", {
        body: {
          action: "rename_wallet",
          user_id: user.id,
          wallet_id: walletId,
          new_name: newName,
        },
      });
      
      if (response.data?.success) {
        await refreshWallets();
        return true;
      }
      return false;
    } catch (error) {
      console.error("Rename wallet error:", error);
      return false;
    }
  };

  const verifyBackup = async (words: string, walletId?: string) => {
    if (!user) return false;
    
    try {
      const response = await supabase.functions.invoke("auth-wallet", {
        body: {
          action: "verify_backup",
          user_id: user.id,
          wallet_id: walletId,
          mnemonic_words: words,
        },
      });
      
      if (response.data?.success) {
        await refreshWallets();
        return true;
      }
      return false;
    } catch (error) {
      console.error("Verify backup error:", error);
      return false;
    }
  };

  const setupPin = async (pin: string) => {
    if (!user) return false;
    
    try {
      const response = await supabase.functions.invoke("auth-wallet", {
        body: {
          action: "setup_pin",
          user_id: user.id,
          pin,
        },
      });
      
      if (response.data?.success) {
        await refreshSecurity();
        return true;
      }
      return false;
    } catch (error) {
      console.error("Setup PIN error:", error);
      return false;
    }
  };

  const verifyPin = async (pin: string) => {
    if (!user) return { success: false, error: "Not authenticated" };
    
    try {
      const response = await supabase.functions.invoke("auth-wallet", {
        body: {
          action: "verify_pin",
          user_id: user.id,
          pin,
        },
      });
      
      return response.data || { success: false };
    } catch (error) {
      console.error("Verify PIN error:", error);
      return { success: false, error: "Verification failed" };
    }
  };

  const setupTotp = async () => {
    if (!user) return null;
    
    try {
      const response = await supabase.functions.invoke("auth-wallet", {
        body: {
          action: "setup_totp",
          user_id: user.id,
        },
      });
      
      if (response.data?.success) {
        return {
          secret: response.data.secret,
          qr_uri: response.data.qr_uri,
        };
      }
      return null;
    } catch (error) {
      console.error("Setup TOTP error:", error);
      return null;
    }
  };

  const verifyTotp = async (code: string) => {
    if (!user) return false;
    
    try {
      const response = await supabase.functions.invoke("auth-wallet", {
        body: {
          action: "verify_totp",
          user_id: user.id,
          code,
        },
      });
      
      if (response.data?.success) {
        await refreshSecurity();
        return true;
      }
      return false;
    } catch (error) {
      console.error("Verify TOTP error:", error);
      return false;
    }
  };

  // Verify TOTP code without updating security settings (for login verification)
  const verifyTotpCode = async (code: string) => {
    if (!user) return false;
    
    try {
      const response = await supabase.functions.invoke("auth-wallet", {
        body: {
          action: "verify_totp_code",
          user_id: user.id,
          code,
        },
      });
      
      return response.data?.success || false;
    } catch (error) {
      console.error("Verify TOTP code error:", error);
      return false;
    }
  };

  const disablePin = async (pin: string) => {
    if (!user) return { success: false, error: "Not authenticated" };
    
    try {
      const response = await supabase.functions.invoke("auth-wallet", {
        body: {
          action: "disable_pin",
          user_id: user.id,
          pin,
        },
      });
      
      if (response.data?.success) {
        await refreshSecurity();
        return { success: true };
      }
      return { success: false, error: response.data?.error || "Failed to disable PIN" };
    } catch (error) {
      console.error("Disable PIN error:", error);
      return { success: false, error: "Failed to disable PIN" };
    }
  };

  const disableTotp = async (code: string) => {
    if (!user) return { success: false, error: "Not authenticated" };
    
    try {
      const response = await supabase.functions.invoke("auth-wallet", {
        body: {
          action: "disable_totp",
          user_id: user.id,
          code,
        },
      });
      
      if (response.data?.success) {
        await refreshSecurity();
        return { success: true };
      }
      return { success: false, error: response.data?.error || "Failed to disable 2FA" };
    } catch (error) {
      console.error("Disable TOTP error:", error);
      return { success: false, error: "Failed to disable 2FA" };
    }
  };

  const exportSeed = async (pin?: string, walletId?: string) => {
    if (!user) return null;
    
    try {
      const response = await supabase.functions.invoke("auth-wallet", {
        body: {
          action: "export_seed",
          user_id: user.id,
          wallet_id: walletId,
          encryption_key: getEncryptionKey(user.id),
          pin,
        },
      });
      
      if (response.data?.success) {
        return response.data.mnemonic;
      }
      return null;
    } catch (error) {
      console.error("Export seed error:", error);
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        walletsLoading,
        wallet,
        wallets,
        security,
        securityVerified,
        setSecurityVerified,
        signUp,
        signIn,
        signOut,
        generateWallet,
        importWallet,
        switchWallet,
        deleteWallet,
        renameWallet,
        verifyBackup,
        setupPin,
        disablePin,
        verifyPin,
        setupTotp,
        verifyTotp,
        verifyTotpCode,
        disableTotp,
        exportSeed,
        refreshWallet,
        refreshWallets,
        refreshSecurity,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};